/**
 * Sample stationary points for the signed-in screen.
 * Replace this module when the stationary points API is connected.
 * The screen already swaps in skeletons while `loading` is true.
 */

export type PointColor = 'red' | 'blue' | 'purple' | 'green' | 'orange' | 'yellow';

export interface PointActivity {
  id: string;
  title: string;
  time: string;
  tone: 'blue' | 'green' | 'red' | 'purple';
}

export interface StationaryPoint {
  id: string;
  name: string;
  address: string;
  city: string;
  latitude: number;
  longitude: number;
  accuracyMeters: number;
  timesUsed: number;
  lastUsedHours: number | null;
  totalMockHours: number;
  favourite: boolean;
  notes: string;
  createdLabel: string;
  color: PointColor;
  collectionIds: string[];
  playlistIds: string[];
  activity: PointActivity[];
  usage: number[];
}

export interface MockPlaylist {
  id: string;
  name: string;
}

export interface PointCollection {
  id: string;
  name: string;
  color: string;
}

export const POINT_COLOR: Record<PointColor, string> = {
  red: '#ef4444',
  blue: '#3b82f6',
  purple: '#8b5cf6',
  green: '#22c55e',
  orange: '#f97316',
  yellow: '#eab308',
};

export const USAGE_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export const PLAYLISTS: MockPlaylist[] = [
  { id: 'delhi-tour', name: 'Delhi Tour' },
  { id: 'office-loop', name: 'Office Loop' },
  { id: 'weekend-walk', name: 'Weekend Walk' },
];

export const COLLECTIONS: PointCollection[] = [
  { id: 'delhi-tour', name: 'Delhi Tour', color: '#7c5cff' },
  { id: 'work', name: 'Work', color: '#3b82f6' },
  { id: 'weekend', name: 'Weekend', color: '#22c55e' },
];

const CITIES = [
  { city: 'New Delhi', region: 'Delhi', latitude: 28.6139, longitude: 77.209 },
  { city: 'Noida', region: 'Uttar Pradesh', latitude: 28.5355, longitude: 77.391 },
  { city: 'Ghaziabad', region: 'Uttar Pradesh', latitude: 28.6692, longitude: 77.4538 },
  { city: 'Gurugram', region: 'Haryana', latitude: 28.4595, longitude: 77.0266 },
  { city: 'Faridabad', region: 'Haryana', latitude: 28.4089, longitude: 77.3178 },
  { city: 'Greater Noida', region: 'Uttar Pradesh', latitude: 28.4744, longitude: 77.504 },
  { city: 'Dwarka', region: 'Delhi', latitude: 28.5921, longitude: 77.046 },
  { city: 'Rohini', region: 'Delhi', latitude: 28.7495, longitude: 77.0565 },
] as const;

export const CITY_OPTIONS = CITIES.map((city) => city.city);

const COLORS: PointColor[] = ['red', 'blue', 'purple', 'green', 'orange', 'yellow'];

interface PointInput {
  id: string;
  name: string;
  address: string;
  city: string;
  latitude: number;
  longitude: number;
  accuracyMeters?: number;
  timesUsed?: number;
  lastUsedHours?: number | null;
  totalMockHours?: number;
  favourite?: boolean;
  notes?: string;
  createdLabel?: string;
  color?: PointColor;
  collectionIds?: string[];
  playlistIds?: string[];
  activity?: PointActivity[];
  usage?: number[];
}

function defaultActivity(createdLabel: string, used: boolean): PointActivity[] {
  if (!used) {
    return [{ id: 'created', title: 'Point created', time: createdLabel, tone: 'purple' }];
  }
  return [
    { id: 'mock', title: 'Mock started', time: 'Today, 4:15 PM', tone: 'blue' },
    { id: 'update', title: 'Location updated', time: 'Yesterday, 10:22 AM', tone: 'green' },
    { id: 'saved', title: 'Saved to a collection', time: '20 Oct 2026, 6:40 PM', tone: 'red' },
    { id: 'created', title: 'Point created', time: createdLabel, tone: 'purple' },
  ];
}

function defaultUsage(timesUsed: number, seed: number): number[] {
  return USAGE_DAYS.map((_, day) => {
    if (timesUsed === 0) return 0;
    return ((seed + day * 5) % 7) * 8 + (timesUsed % 5) * 3;
  });
}

export function createStationaryPoint(input: PointInput): StationaryPoint {
  const timesUsed = input.timesUsed ?? 0;
  const createdLabel = input.createdLabel ?? '23 Oct 2026, 9:00 AM';
  return {
    id: input.id,
    name: input.name,
    address: input.address,
    city: input.city,
    latitude: input.latitude,
    longitude: input.longitude,
    accuracyMeters: input.accuracyMeters ?? 5,
    timesUsed,
    lastUsedHours: input.lastUsedHours === undefined ? null : input.lastUsedHours,
    totalMockHours: input.totalMockHours ?? Math.round(timesUsed * 0.4 * 10) / 10,
    favourite: input.favourite ?? false,
    notes: input.notes ?? '',
    createdLabel,
    color: input.color ?? 'red',
    collectionIds: input.collectionIds ?? [],
    playlistIds: input.playlistIds ?? [],
    activity: input.activity ?? defaultActivity(createdLabel, timesUsed > 0),
    usage: input.usage ?? defaultUsage(timesUsed, input.name.length),
  };
}

function jitter(index: number, base: number, span: number): number {
  const wave = Math.sin(index * 12.9898) * 43758.5453;
  const fraction = wave - Math.floor(wave);
  return Math.round((base + (fraction - 0.5) * span) * 10000) / 10000;
}

const HOME_ACTIVITY: PointActivity[] = [
  { id: 'mock', title: 'Mock started', time: 'Today, 4:15 PM', tone: 'blue' },
  { id: 'update', title: 'Location updated', time: 'Yesterday, 10:22 AM', tone: 'green' },
  { id: 'playlist', title: 'Added to Delhi Tour', time: '20 Oct 2026, 6:40 PM', tone: 'red' },
  { id: 'created', title: 'Point created', time: '15 Oct 2026, 9:12 AM', tone: 'purple' },
];

const FEATURED: PointInput[] = [
  {
    id: 'home',
    name: 'Home',
    address: 'New Delhi, Delhi, India',
    city: 'New Delhi',
    latitude: 28.6139,
    longitude: 77.209,
    accuracyMeters: 5,
    timesUsed: 18,
    lastUsedHours: 2,
    totalMockHours: 12.5,
    favourite: true,
    notes: 'Primary pin for home. Used for daily mock checks.',
    createdLabel: '15 Oct 2026, 9:12 AM',
    color: 'red',
    collectionIds: ['delhi-tour'],
    playlistIds: ['delhi-tour'],
    activity: HOME_ACTIVITY,
  },
  {
    id: 'office',
    name: 'Office',
    address: 'Noida, Uttar Pradesh, India',
    city: 'Noida',
    latitude: 28.5355,
    longitude: 77.391,
    accuracyMeters: 10,
    timesUsed: 8,
    lastUsedHours: 24,
    totalMockHours: 4.2,
    color: 'blue',
    collectionIds: ['work'],
    notes: 'Weekday office pin.',
    createdLabel: '02 Oct 2026, 11:05 AM',
  },
  {
    id: 'india-gate',
    name: 'India Gate',
    address: 'New Delhi, Delhi, India',
    city: 'New Delhi',
    latitude: 28.6129,
    longitude: 77.2295,
    accuracyMeters: 10,
    timesUsed: 12,
    lastUsedHours: 72,
    totalMockHours: 6,
    favourite: true,
    color: 'purple',
    collectionIds: ['delhi-tour'],
    notes: 'Central Delhi landmark pin.',
    createdLabel: '28 Sep 2026, 4:40 PM',
  },
  {
    id: 'connaught',
    name: 'Connaught Place',
    address: 'New Delhi, Delhi, India',
    city: 'New Delhi',
    latitude: 28.6315,
    longitude: 77.2167,
    accuracyMeters: 5,
    timesUsed: 6,
    lastUsedHours: 120,
    totalMockHours: 2.5,
    color: 'green',
    collectionIds: ['delhi-tour'],
    createdLabel: '20 Sep 2026, 1:15 PM',
  },
  {
    id: 'gym',
    name: 'Gym',
    address: 'Lajpat Nagar, New Delhi, India',
    city: 'New Delhi',
    latitude: 28.5691,
    longitude: 77.2432,
    accuracyMeters: 10,
    timesUsed: 4,
    lastUsedHours: 168,
    totalMockHours: 1.4,
    color: 'orange',
    collectionIds: ['delhi-tour'],
    createdLabel: '11 Sep 2026, 7:30 AM',
  },
];

interface ExtraInput {
  name: string;
  cityIndex: number;
  lastUsedHours: number | null;
  timesUsed: number;
  accuracyMeters: number;
  favourite?: boolean;
  color: PointColor;
  collectionIds?: string[];
}

const RECENT: ExtraInput[] = [
  { name: 'Airport', cityIndex: 3, lastUsedHours: 6, timesUsed: 9, accuracyMeters: 10, color: 'blue', collectionIds: ['delhi-tour'] },
  { name: 'Select Citywalk', cityIndex: 0, lastUsedHours: 10, timesUsed: 7, accuracyMeters: 5, favourite: true, color: 'purple' },
  { name: 'Cyber Hub', cityIndex: 2, lastUsedHours: 20, timesUsed: 11, accuracyMeters: 10, color: 'green' },
  { name: 'Akshardham', cityIndex: 1, lastUsedHours: 30, timesUsed: 5, accuracyMeters: 5, favourite: true, color: 'orange', collectionIds: ['work'] },
  { name: 'Pacific Mall', cityIndex: 4, lastUsedHours: 48, timesUsed: 3, accuracyMeters: 15, favourite: true, color: 'red' },
  { name: 'Sector 18', cityIndex: 5, lastUsedHours: 80, timesUsed: 2, accuracyMeters: 10, color: 'yellow' },
  { name: 'City Centre', cityIndex: 6, lastUsedHours: 100, timesUsed: 4, accuracyMeters: 5, color: 'blue' },
];

const NEVER: ExtraInput[] = [
  { name: 'Draft Pin', cityIndex: 7, lastUsedHours: null, timesUsed: 0, accuracyMeters: 5, color: 'yellow' },
  { name: 'Spare Lot', cityIndex: 4, lastUsedHours: null, timesUsed: 0, accuracyMeters: 10, color: 'green' },
  { name: 'Empty Plot', cityIndex: 5, lastUsedHours: null, timesUsed: 0, accuracyMeters: 5, color: 'orange' },
  { name: 'Test Corner', cityIndex: 6, lastUsedHours: null, timesUsed: 0, accuracyMeters: 20, color: 'purple' },
  { name: 'Quiet Lane', cityIndex: 2, lastUsedHours: null, timesUsed: 0, accuracyMeters: 5, color: 'red' },
  { name: 'Unused Gate', cityIndex: 3, lastUsedHours: null, timesUsed: 0, accuracyMeters: 10, color: 'blue' },
];

const OLD_NAMES = [
  'Hauz Khas',
  'Khan Market',
  'Lotus Temple',
  'Qutub Minar',
  'Red Fort',
  'Chandni Chowk',
  'Nehru Place',
  'Saket',
  'Vasant Kunj',
  'Rajouri Garden',
  'Karol Bagh',
  'Pitampura',
  'Model Town',
  'Civil Lines',
  'Kashmere Gate',
  'Anand Vihar',
  'Mayur Vihar',
  'Kalkaji',
  'Green Park',
  'Malviya Nagar',
  'Janakpuri',
  'Tilak Nagar',
  'Patel Nagar',
  'Dhaula Kuan',
];

function fromExtra(extra: ExtraInput, index: number): StationaryPoint {
  const city = CITIES[extra.cityIndex];
  return createStationaryPoint({
    id: extra.name.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
    name: extra.name,
    address: `${extra.name}, ${city.city}, ${city.region}, India`,
    city: city.city,
    latitude: jitter(index + 3, city.latitude, 0.03),
    longitude: jitter(index + 11, city.longitude, 0.03),
    accuracyMeters: extra.accuracyMeters,
    timesUsed: extra.timesUsed,
    lastUsedHours: extra.lastUsedHours,
    favourite: extra.favourite ?? false,
    color: extra.color,
    collectionIds: extra.collectionIds,
    createdLabel: '01 Sep 2026, 10:00 AM',
  });
}

function fromOld(name: string, index: number): StationaryPoint {
  const city = CITIES[index % CITIES.length];
  const collectionIds = index % 4 === 0 ? ['weekend'] : index % 4 === 1 ? ['work'] : [];
  return createStationaryPoint({
    id: name.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
    name,
    address: `${name}, ${city.city}, ${city.region}, India`,
    city: city.city,
    latitude: jitter(index + 21, city.latitude, 0.045),
    longitude: jitter(index + 37, city.longitude, 0.045),
    accuracyMeters: [5, 10, 15, 20][index % 4],
    timesUsed: 2 + (index % 6),
    lastUsedHours: 240 + index * 30,
    favourite: index < 3,
    color: COLORS[index % COLORS.length],
    collectionIds,
    createdLabel: '12 Aug 2026, 3:20 PM',
  });
}

export const STATIONARY_POINTS: StationaryPoint[] = [
  ...FEATURED.map((point) => createStationaryPoint(point)),
  ...RECENT.map(fromExtra),
  ...NEVER.map(fromExtra),
  ...OLD_NAMES.map(fromOld),
];

export function isUsedThisWeek(point: StationaryPoint): boolean {
  return point.timesUsed > 0 && point.lastUsedHours != null && point.lastUsedHours <= 168;
}

export function isNeverUsed(point: StationaryPoint): boolean {
  return point.timesUsed === 0;
}

export function isMostUsed(point: StationaryPoint): boolean {
  return point.timesUsed >= 8;
}

export interface SessionBucket {
  label: string;
  count: number;
  color: string;
}

export interface PointInsights {
  weekUses: number;
  hourDelta: number;
  avgMinutes: number;
  avgDelta: number;
  buckets: SessionBucket[];
  trend: { label: string; value: number }[];
}

const TREND_LABELS = ['22 Sep', '27 Sep', '2 Oct', '7 Oct', '12 Oct', '17 Oct', '22 Oct'];

function splitSessions(total: number): number[] {
  if (total <= 0) return [0, 0, 0, 0];
  const short = Math.round(total * 0.33);
  const mid = Math.round(total * 0.39);
  const long = Math.round(total * 0.22);
  return [short, mid, long, Math.max(0, total - short - mid - long)];
}

export function pointInsights(point: StationaryPoint): PointInsights {
  const avgMinutes = point.timesUsed === 0 ? 0 : Math.round((point.totalMockHours * 60) / point.timesUsed);
  const counts = point.id === 'home' ? [6, 7, 4, 1] : splitSessions(point.timesUsed);
  const labels = ['< 10 min', '10–30 min', '30–60 min', '> 60 min'];
  const colors = ['#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444'];
  const homeTrend = [10, 14, 18, 12, 22, 16, 26];
  return {
    weekUses: point.id === 'home' ? 4 : Math.min(6, point.timesUsed % 5),
    hourDelta: point.id === 'home' ? 2.3 : Math.round(point.totalMockHours * 18) / 100,
    avgMinutes,
    avgDelta: point.id === 'home' ? -12 : ((point.name.length % 7) - 3) * 4,
    buckets: labels.map((label, index) => ({ label, count: counts[index], color: colors[index] })),
    trend: TREND_LABELS.map((label, index) => ({
      label,
      value: point.timesUsed === 0 ? 0 : (point.id === 'home' ? homeTrend[index] : 8 + ((point.name.length * 3 + index * 5) % 28)),
    })),
  };
}

export function formatLastUsed(hours: number | null): string {
  if (hours == null) return 'Never';
  if (hours < 1) return 'Just now';
  if (hours < 24) {
    const rounded = Math.round(hours);
    return rounded === 1 ? '1 hour ago' : `${rounded} hours ago`;
  }
  const days = Math.round(hours / 24);
  if (days < 7) return days === 1 ? '1 day ago' : `${days} days ago`;
  const weeks = Math.max(1, Math.round(days / 7));
  return weeks === 1 ? '1 week ago' : `${weeks} weeks ago`;
}

export function formatMockTime(hours: number): string {
  if (hours < 1) return `${Math.max(1, Math.round(hours * 60))} m`;
  const rounded = Math.round(hours * 10) / 10;
  return `${rounded} h`;
}

export function formatAccuracy(meters: number): string {
  return `${meters} m`;
}

export function formatCoords(latitude: number, longitude: number): string {
  return `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`;
}

export function pointShareUrl(point: Pick<StationaryPoint, 'latitude' | 'longitude' | 'address'>): string {
  const params = new URLSearchParams({
    lat: String(point.latitude),
    lng: String(point.longitude),
    address: point.address,
  });
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://mockloc.netlify.app';
  return `${origin}/dl-stpoint?${params.toString()}`;
}

export function pointAppUri(point: Pick<StationaryPoint, 'latitude' | 'longitude' | 'address'>): string {
  return `mockloc://stpoint?lat=${encodeURIComponent(String(point.latitude))}&lng=${encodeURIComponent(String(point.longitude))}&address=${encodeURIComponent(point.address)}`;
}

function csvCell(value: string | number): string {
  const text = String(value);
  if (/[",\n]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
  return text;
}

export function pointsToCsv(points: StationaryPoint[]): string {
  const header = ['name', 'latitude', 'longitude', 'address', 'city', 'accuracy', 'notes'];
  const rows = points.map((point) => [
    csvCell(point.name),
    csvCell(point.latitude),
    csvCell(point.longitude),
    csvCell(point.address),
    csvCell(point.city),
    csvCell(point.accuracyMeters),
    csvCell(point.notes),
  ].join(','));
  return [header.join(','), ...rows].join('\n');
}

function splitCsvLine(line: string): string[] {
  const cells: string[] = [];
  let current = '';
  let quoted = false;
  for (let index = 0; index < line.length; index += 1) {
    const char = line[index];
    if (char === '"') {
      if (quoted && line[index + 1] === '"') {
        current += '"';
        index += 1;
      } else {
        quoted = !quoted;
      }
    } else if (char === ',' && !quoted) {
      cells.push(current);
      current = '';
    } else {
      current += char;
    }
  }
  cells.push(current);
  return cells;
}

export function parseStationaryCsv(text: string): StationaryPoint[] {
  const lines = text.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
  if (lines.length === 0) return [];
  const rows = lines.map(splitCsvLine);
  const start = rows[0][0]?.trim().toLowerCase() === 'name' ? 1 : 0;
  const stamp = Date.now();
  const parsed: StationaryPoint[] = [];
  rows.slice(start).forEach((cells, index) => {
    const name = cells[0]?.trim();
    const latitude = Number(cells[1]);
    const longitude = Number(cells[2]);
    if (!name || !Number.isFinite(latitude) || !Number.isFinite(longitude)) return;
    if (latitude < -90 || latitude > 90 || longitude < -180 || longitude > 180) return;
    const accuracy = Number(cells[5]);
    parsed.push(createStationaryPoint({
      id: `csv-${stamp}-${index}`,
      name,
      latitude,
      longitude,
      address: cells[3]?.trim() || `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`,
      city: cells[4]?.trim() || 'Unknown',
      accuracyMeters: Number.isFinite(accuracy) ? Math.min(500, Math.max(1, Math.round(accuracy))) : 5,
      notes: cells[6]?.trim() || '',
      color: COLORS[index % COLORS.length],
      timesUsed: 0,
      lastUsedHours: null,
    }));
  });
  return parsed;
}
